<script lang="ts">
import { defineComponent } from 'vue';
import { Menu, Search } from 'lucide-vue-next';

import { deviceWidths } from '../../constants';

import ActiveGiftCard from '@/components/header/ActiveGiftCart.vue';
import MobileHeaderMenu from '@/components/header/MobileHeaderMenu.vue';
import FavoritesHeaderLink from '@/components/header/FavoritesHeaderLink.vue';

export default defineComponent({
  components: {
    ActiveGiftCard,
    MobileHeaderMenu,
    FavoritesHeaderLink,
    Menu,
    Search
  },
  props: {
    logoUrl: {
      type: String,
      required: true
    },
    menuLogoUrl: {
      type: String,
      default: ''
    },
    links: {
      type: Array,
      required: true
    },
    inverted: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    showMenu: false
  }),
  computed: {
    hasAnyGiftCards(): boolean {
      return (this as any).$activeGiftCard?.giftCards?.length > 0;
    },
    rootPathRedirectUrl(): string {
      return (this as any).$channel?.rootPathRedirectUrl || '';
    },
    onRootPath(): boolean {
      return (this as any).$route?.pathname === this.rootPathRedirectUrl;
    },
    countryCode(): string {
      const list = (this as any).$countrySwitcher;
      const sysId = (this as any).$channel?.systemId;
      if (Array.isArray(list) && list.length) {
        // Match the country to the channel we're on (authoritative), falling
        // back to the `current` flag.
        const match =
          (sysId && list.find((c: any) => c.channelId === sysId)) ||
          list.find((c: any) => c.current);
        const id = match?.id;
        if (id) {
          return id === '001' ? 'eu' : id.toLowerCase();
        }
      }
      const ch = (this as any).$channel;
      return (ch?.id || ch?.code || 'se').toLowerCase();
    },
    flagSrc(): string {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
        return require(`@/static/flags/${this.countryCode}.svg`);
      } catch {
        return '';
      }
    }
  },
  watch: {
    '$route.pathname'() {
      this.closeMenu();
    },
    showMenu(open: boolean) {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = open ? 'hidden' : '';
      }
    }
  },
  beforeUnmount() {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  },
  methods: {
    openMenu() {
      this.showMenu = true;
      setTimeout(() => {
        window.addEventListener('resize', this.closeMenuIfMobileViewportBoundariesAreExceeded);
        window.addEventListener('keydown', this.onKeyDown);
      }, 0);
    },
    closeMenuIfMobileViewportBoundariesAreExceeded() {
      const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
      if (width > deviceWidths.tabletMaxWidth) {
        this.closeMenu();
      }
    },
    closeMenu() {
      if (!this.showMenu) return;
      this.showMenu = false;
      window.removeEventListener('resize', this.closeMenuIfMobileViewportBoundariesAreExceeded);
      window.removeEventListener('keydown', this.onKeyDown);
    },
    onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeMenu();
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
});
</script>

<template>
  <div class="relative w-full">
    <div class="flex h-14 items-center gap-3 px-4" :class="inverted ? 'text-background' : 'text-foreground'">
      <button type="button" class="btn-bare -ml-2 inline-flex items-center gap-2 p-2 text-inherit" aria-label="Open menu" @click="openMenu()">
        <Menu class="h-6 w-6" />
        <span v-if="flagSrc" class="inline-flex h-[18px] w-[18px] shrink-0 overflow-hidden rounded-full">
          <img :src="flagSrc" :alt="countryCode" class="h-full w-full object-cover" />
        </span>
      </button>

      <router-link v-if="!onRootPath" :to="rootPathRedirectUrl" class="flex justify-center">
        <img class="mt-[5px] h-7 max-w-none" :src="logoUrl" alt="logo" />
      </router-link>
      <img v-else class="mt-[5px] h-7 max-w-none cursor-pointer" :src="logoUrl" alt="logo" @click="scrollToTop" />

      <div v-if="hasAnyGiftCards" class="relative ml-auto flex items-center gap-3">
        <favorites-header-link class="relative z-10 -m-3 p-3 text-inherit" />
        <button
          type="button"
          aria-label="Search"
          class="btn-bare relative z-10 -m-3 p-3 text-inherit transition-colors hover:opacity-70"
          @click="$mitt.emit('Search:Open')"
        >
          <Search class="h-5 w-5" />
        </button>
        <active-gift-card class="relative z-10 -m-3 p-3 text-inherit" />
      </div>
    </div>

    <Transition name="mobile-menu-overlay">
      <div v-if="showMenu" class="fixed inset-0 z-40 bg-black/50" @click="closeMenu()" />
    </Transition>

    <Transition name="mobile-menu-panel">
      <mobile-header-menu v-if="showMenu" :links="links" :logo-url="menuLogoUrl || logoUrl" @close="closeMenu" />
    </Transition>
  </div>
</template>

<style>
.mobile-menu-overlay-enter-active,
.mobile-menu-overlay-leave-active {
  transition: opacity 0.3s ease;
}
.mobile-menu-overlay-enter-from,
.mobile-menu-overlay-leave-to {
  opacity: 0;
}

.mobile-menu-panel-enter-active,
.mobile-menu-panel-leave-active {
  transition: transform 0.3s ease-in-out;
}
.mobile-menu-panel-enter-from,
.mobile-menu-panel-leave-to {
  transform: translateX(-100%);
}
</style>
